import { ColumnsType } from 'rc-table/lib/interface';
import React from 'react';
import { DataTableType, Table, TableCell, TableHeadCell } from '@/components/common/Table';
import { CryptoFrontendStatus, useGetBuyCryptoTxs } from '@/api/queryHooks/useTransactionsController';
import { formatDateAndTime, formatNumberWithCommas, truncateString } from '@/utils/functions';
import { BuyCryptoTxnResponse, CryptoCurrency, FiatCurrency } from '@/api/common.types';
import map from 'lodash/map';
import { mapTxTypeDtoToTxType } from '@/mappers';
import { BTC_NETWORK, ETH_NETWORK } from '@/constants/common';
import { useTableHelpers } from '@/components/common/Table/useTableHelpers';
import { RowStatus } from '@/views/Transactions/components/TransactionsTable/RowStatus';
import { RowTxnButton } from '@/views/Transactions/components/TransactionsTable/RowTxnButton';
import { AmountAndCurrency } from '@/views/Transactions/components/TransactionsTable/types';
import { CopyButton } from '@/components/common/buttons';

import styles from './TransactionsTableCrypto.module.css';

function mapBuyCryptoData(data?: BuyCryptoTxnResponse[]): DataTableType[] {
  return map(data, (item) => {
    const { date, time } = formatDateAndTime(item.created);
    const txNetwork = item.cryptoCurrency === CryptoCurrency.BTC ? BTC_NETWORK : ETH_NETWORK;

    return {
      key: item.id ?? Math.random(),
      paymentId: item.id?.split('-')[0],
      date: `${time}, ${date}`,
      type: mapTxTypeDtoToTxType(item.paymentType),
      amount: { amount: formatNumberWithCommas(item.fiatAmount ?? 0), currency: item.fiatCurrency },
      crypto: {
        amount: formatNumberWithCommas(item.cryptoAmount ?? 0),
        currency: item.cryptoCurrency,
      },
      status: item.frontendStatus,
      txn: item?.blockChainExplorer
        ? {
            href: `${item?.blockChainExplorer}${item.blockchainTxHash || ''}`,
            txHash: item.blockchainTxHash || '',
          }
        : { href: undefined, txHash: item.blockchainTxHash || '' },
      transferAddress: item?.transferAddress,
    };
  });
}

export const TransactionsTableCrypto = () => {
  const { sortBy, isDesc, toggleSort, page, setPage, tableData, setTableData } = useTableHelpers();
  const { data } = useGetBuyCryptoTxs([
    { sortField: sortBy, isDesc, page },
    {
      keepPreviousData: true,
      refetchOnMount: true,
      onSuccess(data) {
        setTableData(mapBuyCryptoData(data?.content));
      },
    },
  ]);

  const columns: ColumnsType<DataTableType> = [
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('created')} active={sortBy === 'created'} isDesc={isDesc}>
          Date
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'date',
      render: (value: string) => <TableCell>{value}</TableCell>,
      key: 'date',
    },
    {
      title: <TableHeadCell sort={false}>Payment ID</TableHeadCell>,
      align: 'left',
      dataIndex: 'paymentId',
      render: (value: string) => <TableCell isBold>{value}</TableCell>,
      key: 'paymentId',
    },

    {
      title: (
        <TableHeadCell onClick={() => toggleSort('type')} active={sortBy === 'type'} isDesc={isDesc}>
          Type
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'type',
      render: (value: string) => <TableCell>{value}</TableCell>,
      key: 'type',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('fiatAmount')} active={sortBy === 'fiatAmount'} isDesc={isDesc}>
          Amount
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'amount',
      render: (value: AmountAndCurrency<FiatCurrency>) => (
        <TableCell>
          ${value?.amount} {value?.currency}
        </TableCell>
      ),
      key: 'amount',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('cryptoAmount')} active={sortBy === 'cryptoAmount'} isDesc={isDesc}>
          Crypto
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'crypto',
      render: (value: AmountAndCurrency<CryptoCurrency>) => (
        <TableCell>
          {value?.amount} {value?.currency}
        </TableCell>
      ),
      key: 'crypto',
    },
    {
      title: (
        <TableHeadCell
          onClick={() => toggleSort('transferAddress')}
          active={sortBy === 'transferAddress'}
          isDesc={isDesc}
        >
          Wallet Address
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'transferAddress',
      render: (value: string) => (
        <TableCell>
          <CopyButton
            iconPosition="right"
            variants="walletAddress"
            className={styles.copyButton}
            iconClassName={styles.copyButtonIcon}
            valueForCopy={value}
          >
            {truncateString({
              string: value,
              startAmount: 6,
              endAmount: 4,
            })}
          </CopyButton>
        </TableCell>
      ),
      key: 'transferAddress',
    },
    {
      title: (
        <TableHeadCell
          onClick={() => toggleSort('frontendStatus')}
          active={sortBy === 'frontendStatus'}
          isDesc={isDesc}
        >
          Status
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'status',
      render: (value: CryptoFrontendStatus) => (
        <TableCell>
          <RowStatus statusFromBE={value} />
        </TableCell>
      ),
      key: 'status',
    },
    {
      title: <TableHeadCell sort={false}>TXN</TableHeadCell>,
      align: 'right',
      dataIndex: 'txn',
      render: (value: { href: string; txHash?: string }) => (
        <TableCell>
          {value?.href ? (
            <RowTxnButton href={value.href} txHash={value.txHash} />
          ) : (
            <CopyButton iconPosition="right" variants="buyCryptoHash" valueForCopy={value.txHash}>
              {truncateString({
                string: value.txHash,
              })}
            </CopyButton>
          )}
        </TableCell>
      ),
      key: 'txn',
    },
  ];

  return (
    <Table
      data={tableData}
      columns={columns}
      totalElements={data?.totalElements}
      pageSize={data?.size}
      onPaginate={setPage}
    />
  );
};
